import { useState, memo, useMemo, useCallback } from "react";
import { Document, Page } from "react-pdf";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { OutlineItem } from "@/lib/pdfHeadingExtract";
import { OutlineEditor } from "./OutlineEditor";
import { BookOpen, Images, Edit } from "lucide-react";

// 獨立的縮圖元件，使用 memo 只在 pageNum 或 pdfUrl 改變時重新渲染
// isActive 不影響 PDF 渲染，只影響樣式
const PageThumbnail = memo(({
  pageNum,
  isActive,
  onPageClick
}: {
  pageNum: number;
  isActive: boolean;
  onPageClick: (pageNum: number) => void;
}) => {
  return (
    <div
      className={`cursor-pointer rounded-lg border-2 p-2 transition-all hover:border-primary flex flex-col items-center ${
        isActive ? "border-primary bg-primary/10" : "border-border"
      }`}
      onClick={() => onPageClick(pageNum)}
    >
      <Page
        pageNumber={pageNum}
        width={130}
        renderTextLayer={false}
        renderAnnotationLayer={false}
      />
      <p className="mt-2 text-center text-sm text-muted-foreground w-full">
        第 {pageNum} 頁
      </p>
    </div>
  );
}, (prevProps, nextProps) => {
  // 自定義比較函數：只有 pageNum 或 onPageClick 改變時才重新渲染
  // isActive 改變不會觸發重新渲染（只會更新 DOM class）
  return prevProps.pageNum === nextProps.pageNum &&
         prevProps.onPageClick === nextProps.onPageClick;
});

interface ThumbnailPanelProps {
  isOpen: boolean;
  onClose: () => void;
  pdfUrl: string;
  numPages: number;
  currentPage: number;
  onPageSelect: (page: number) => void;
  outline?: OutlineItem[];
  onOutlineUpdate?: (newOutline: OutlineItem[]) => void;
  enableEdit?: boolean; // 是否啟用編輯模式（業主才有）
}

export const ThumbnailPanel = ({
  isOpen,
  onClose,
  pdfUrl,
  numPages,
  currentPage,
  onPageSelect,
  outline = [],
  onOutlineUpdate,
  enableEdit = false,
}: ThumbnailPanelProps) => {
  const [activeTab, setActiveTab] = useState<string>("outline");
  const [isEditing, setIsEditing] = useState(false);

  // 使用 useMemo 快取頁面陣列，避免每次渲染都重新建立
  const pageNumbers = useMemo(
    () => Array.from({ length: numPages }, (_, i) => i + 1),
    [numPages]
  );

  // 使用 useCallback 快取點擊處理函數
  const handlePageClick = useCallback((pageNum: number) => {
    onPageSelect(pageNum);
    onClose();
  }, [onPageSelect, onClose]);

  const handleSaveOutline = (newOutline: OutlineItem[]) => {
    onOutlineUpdate?.(newOutline);
    setIsEditing(false);
  };

  const handleSheetChange = (open: boolean) => {
    if (!open) {
      setIsEditing(false);
      onClose();
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleSheetChange}>
      <SheetContent side="left" className="w-[280px] sm:w-[400px]">
        <SheetHeader>
          <SheetTitle>導覽</SheetTitle>
        </SheetHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="outline" className="flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              書籍章節
            </TabsTrigger>
            <TabsTrigger value="thumbnails" className="flex items-center gap-2">
              <Images className="h-4 w-4" />
              頁面縮圖
            </TabsTrigger>
          </TabsList>

          {/* 書籍章節 */}
          <TabsContent value="outline" className="mt-4">
            {isEditing ? (
              <div className="h-[calc(100vh-240px)] md:h-[calc(100vh-280px)] lg:h-[calc(100vh-150px)]">
                <OutlineEditor
                  outline={outline}
                  numPages={numPages}
                  onSave={handleSaveOutline}
                  onCancel={() => setIsEditing(false)}
                />
              </div>
            ) : (
              <div className="flex flex-col h-[calc(100vh-240px)] md:h-[calc(100vh-280px)] lg:h-[calc(100vh-150px)]">
                <ScrollArea className="flex-1">
                  <div className="space-y-1 pr-4">
                    {outline.length === 0 ? (
                      <p className="text-center text-sm text-muted-foreground py-8">
                        此 PDF 沒有內建目錄
                      </p>
                    ) : (
                      outline.map((item, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            onPageSelect(item.pageNumber);
                            onClose();
                          }}
                          className="w-full text-left px-3 py-2 rounded-md text-sm transition-colors"
                        >
                          <span className="line-clamp-2">{item.title}</span>
                        </button>
                      ))
                    )}
                  </div>
                </ScrollArea>

                {enableEdit && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsEditing(true)}
                    className="mt-4 w-full"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    編輯目錄
                  </Button>
                )}
              </div>
            )}
          </TabsContent>

          {/* 頁面縮圖 */}
          <TabsContent value="thumbnails" className="mt-4">
            <ScrollArea className="h-[calc(100vh-180px)]">
              <Document file={pdfUrl}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pr-4">
                  {pageNumbers.map((pageNum) => (
                    <PageThumbnail
                      key={pageNum}
                      pageNum={pageNum}
                      isActive={currentPage === pageNum}
                      onPageClick={handlePageClick}
                    />
                  ))}
                </div>
              </Document>
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
};
